import { useState, useMemo } from 'react';

/**
 * Alert severity levels
 */
export const ALERT_SEVERITY = {
  CRITICAL: 'critical',
  WARNING: 'warning',
  INFO: 'info',
} as const;

export type AlertSeverity = typeof ALERT_SEVERITY[keyof typeof ALERT_SEVERITY];

/**
 * Alert types
 */
export const ALERT_TYPES = {
  CONTEXT_LIMIT_APPROACHING: 'context_limit_approaching',
  CONTEXT_LIMIT_EXCEEDED: 'context_limit_exceeded',
  HIGH_PRUNE_FREQUENCY: 'high_prune_frequency',
  LOW_CACHE_HIT_RATE: 'low_cache_hit_rate',
  MEMORY_SPIKE: 'memory_spike',
} as const;

export type AlertType = typeof ALERT_TYPES[keyof typeof ALERT_TYPES];

interface MemoryAlert {
  id: AlertType;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  count?: number;
  value?: number;
}

interface UtilizationLevel {
  level: string;
  color: string;
  percentage: number;
}

interface UtilizationColorClasses {
  bg: string;
  text: string;
  bgLight: string;
  border: string;
}

/**
 * Custom hook for detecting and managing memory alerts
 */
export const useMemoryAlerts = (stats: Record<string, any> | null | undefined, snapshots: Record<string, any>[] | null | undefined) => {
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<AlertType>>(new Set());

  const alerts = useMemo((): MemoryAlert[] => {
    const newAlerts: MemoryAlert[] = [];

    if (!stats && !snapshots) return newAlerts;

    // Check for context limit events
    if ((snapshots?.length ?? 0) > 0) {
      const highUtilization = snapshots!.filter(s => {
        if (!s.token_count || !s.max_tokens) return false;
        return s.token_count >= 0.9 * s.max_tokens;
      });

      const criticalUtilization = snapshots!.filter(s => {
        if (!s.token_count || !s.max_tokens) return false;
        return s.token_count >= s.max_tokens;
      });

      if (criticalUtilization.length > 0) {
        newAlerts.push({
          id: ALERT_TYPES.CONTEXT_LIMIT_EXCEEDED,
          type: ALERT_TYPES.CONTEXT_LIMIT_EXCEEDED,
          severity: ALERT_SEVERITY.CRITICAL,
          title: 'Context Limit Exceeded',
          message: `${criticalUtilization.length} snapshot(s) have exceeded the context window limit`,
          count: criticalUtilization.length,
        });
      } else if (highUtilization.length > 0) {
        newAlerts.push({
          id: ALERT_TYPES.CONTEXT_LIMIT_APPROACHING,
          type: ALERT_TYPES.CONTEXT_LIMIT_APPROACHING,
          severity: ALERT_SEVERITY.WARNING,
          title: 'High Memory Utilization',
          message: `${highUtilization.length} snapshot(s) are near context window limits (>90%)`,
          count: highUtilization.length,
        });
      }
    }

    // Check for high prune frequency
    if (stats?.by_operation?.prune > 10) {
      const pruneCount = stats!.by_operation.prune;
      newAlerts.push({
        id: ALERT_TYPES.HIGH_PRUNE_FREQUENCY,
        type: ALERT_TYPES.HIGH_PRUNE_FREQUENCY,
        severity: ALERT_SEVERITY.WARNING,
        title: 'Frequent Memory Pruning',
        message: `${pruneCount} prune operations detected. Consider increasing context limits.`,
        count: pruneCount,
      });
    }

    // Check for low cache hit rate
    if (stats?.cache_hit_rate != null && stats.cache_hit_rate < 50) {
      const cacheHitRate = stats.cache_hit_rate;
      newAlerts.push({
        id: ALERT_TYPES.LOW_CACHE_HIT_RATE,
        type: ALERT_TYPES.LOW_CACHE_HIT_RATE,
        severity: ALERT_SEVERITY.INFO,
        title: 'Low Cache Efficiency',
        message: `Cache hit rate is ${cacheHitRate.toFixed(1)}%. Consider optimizing tool cache usage.`,
        value: cacheHitRate,
      });
    }

    // Filter out dismissed alerts
    return newAlerts.filter(alert => !dismissedAlerts.has(alert.id));
  }, [stats, snapshots, dismissedAlerts]);

  const dismissAlert = (alertId: AlertType): void => {
    setDismissedAlerts(prev => new Set(Array.from(prev).concat(alertId)));
  };

  const clearDismissed = (): void => {
    setDismissedAlerts(new Set());
  };

  const hasAlerts = alerts.length > 0;
  const hasCriticalAlerts = alerts.some(a => a.severity === ALERT_SEVERITY.CRITICAL);
  const hasWarningAlerts = alerts.some(a => a.severity === ALERT_SEVERITY.WARNING);

  return {
    alerts,
    dismissAlert,
    clearDismissed,
    hasAlerts,
    hasCriticalAlerts,
    hasWarningAlerts,
  };
};

/**
 * Calculate utilization level and color
 */
export const getUtilizationLevel = (tokenCount: number | undefined | null, maxTokens: number | undefined | null): UtilizationLevel => {
  if (!tokenCount || !maxTokens) {
    return { level: 'unknown', color: 'gray', percentage: 0 };
  }

  const percentage = (tokenCount / maxTokens) * 100;

  if (percentage >= 100) {
    return { level: 'critical', color: 'red', percentage };
  } else if (percentage >= 90) {
    return { level: 'danger', color: 'red', percentage };
  } else if (percentage >= 70) {
    return { level: 'warning', color: 'yellow', percentage };
  } else {
    return { level: 'healthy', color: 'green', percentage };
  }
};

/**
 * Get color classes based on utilization level
 */
export const getUtilizationColorClasses = (level: string): UtilizationColorClasses => {
  switch (level) {
    case 'critical':
    case 'danger':
      return {
        bg: 'bg-red-500',
        text: 'text-red-600 dark:text-red-400',
        bgLight: 'bg-red-100 dark:bg-red-900/30',
        border: 'border-red-500',
      };
    case 'warning':
      return {
        bg: 'bg-yellow-500',
        text: 'text-yellow-600 dark:text-yellow-400',
        bgLight: 'bg-yellow-100 dark:bg-yellow-900/30',
        border: 'border-yellow-500',
      };
    case 'healthy':
      return {
        bg: 'bg-green-500',
        text: 'text-green-600 dark:text-green-400',
        bgLight: 'bg-green-100 dark:bg-green-900/30',
        border: 'border-green-500',
      };
    default:
      return {
        bg: 'bg-gray-500',
        text: 'text-gray-600 dark:text-gray-400',
        bgLight: 'bg-gray-100 dark:bg-gray-900/30',
        border: 'border-gray-500',
      };
  }
};

export default useMemoryAlerts;
